import * as React from 'react'
import {
  useGetTabularIngestTableRowsQuery,
  type TabularIngestJobDetailTable,
  type TabularIngestTableRows
} from '@axilon-apps/domains-ontology-ui-state'
import { useVirtualizer } from '@tanstack/react-virtual'
import { cn } from '../../lib/utils'
import { designPixelsToRem, designPixelsToScaledPixels } from '../../lib/ui-density'

export interface ExtractedColumn {
  name: string
  type: string
  required?: boolean
}

export interface PageLinks {
  byUrn: ReadonlyMap<string, { pageId: string; fileName: string; pageNumber: number }>
  /** `position` is the row's 0-based index among the table's data rows. */
  open: (pageId: string, position: number) => void
}

/** Rows rendered as plain DOM before the grid switches to virtual scrolling. */
const VIRTUALIZE_FROM = 200
const ROW_HEIGHT_PX = 28
const MIN_COLUMN_PX = 88
const MAX_COLUMN_PX = 320
/** How many rows inform a column's width; enough for typical values, cheap for 5,000. */
const WIDTH_SAMPLE_ROWS = 100
const PREVIEW_ROWS = 20

const formatCount = (value: number) => value.toLocaleString('en-US')
const rowsLabel = (value: number) => `${formatCount(value)} row${value === 1 ? '' : 's'}`

/**
 * Every row one run extracted for a table: the stored output, fetched on
 * demand, in a grid that scrolls on its own up to the api-service's cap.
 * Falls back to the 20-row preview embedded in the result when the run kept
 * no output (runs from before outputs were stored) or it cannot be loaded.
 */
export function ExtractedRowsTable({
  jobId,
  table,
  columns,
  pageLinks
}: {
  jobId: string
  table: TabularIngestJobDetailTable
  columns: ExtractedColumn[]
  /** With it, each row whose `source_page_urn` names one of the run's pages links to that page. */
  pageLinks?: PageLinks
}) {
  const hasOutput = Boolean(table.output)
  const rowsQuery = useGetTabularIngestTableRowsQuery(
    { jobId, table: table.name },
    { skip: !hasOutput }
  )
  const loaded: TabularIngestTableRows | undefined = rowsQuery.data
  const rows = loaded?.rows ?? table.previewRows
  const headers = loaded && loaded.columns.length > 0 ? loaded.columns : columns.map((c) => c.name)
  const total = loaded?.rowCount ?? table.output?.rowCount ?? table.rowCount
  const anyRequired = columns.some((column) => column.required)
  const urnAt = loaded ? loaded.columns.indexOf('source_page_urn') : -1
  const leading = React.useMemo<LeadingColumn | undefined>(
    () =>
      pageLinks && pageLinks.byUrn.size > 0 && urnAt >= 0
        ? {
            header: 'Page',
            render: (row, index) => {
              const page = pageLinks.byUrn.get(row[urnAt] ?? '')
              if (!page) return null
              return (
                <button
                  type="button"
                  className="text-st-info hover:underline"
                  title={`Open ${page.fileName} page ${page.pageNumber} in Pages`}
                  onClick={() => pageLinks.open(page.pageId, index)}
                >
                  p{page.pageNumber}
                </button>
              )
            }
          }
        : undefined,
    [pageLinks, urnAt]
  )

  let status: { tone: 'muted' | 'warn'; text: string }
  if (loaded) {
    status = loaded.truncated
      ? {
          tone: 'warn',
          text: `Showing the first ${formatCount(loaded.limit)} of ${rowsLabel(total)} — the rest are not shown here.`
        }
      : { tone: 'muted', text: `All ${rowsLabel(total)}.` }
  } else if (hasOutput && rowsQuery.isError) {
    status = {
      tone: 'warn',
      text: `Showing the first ${formatCount(Math.min(rows.length, PREVIEW_ROWS))} of ${rowsLabel(total)} — the full table could not be loaded.`
    }
  } else if (hasOutput) {
    status = { tone: 'muted', text: `Loading all ${rowsLabel(total)}…` }
  } else {
    status = {
      tone: 'muted',
      text:
        total > rows.length
          ? `Showing the first ${formatCount(rows.length)} of ${rowsLabel(total)}.`
          : `All ${rowsLabel(total)}.`
    }
  }

  return (
    <div className="min-w-0">
      <div className="mb-1.5 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 text-11">
        <p
          role="status"
          data-testid="extracted-rows-status"
          className={cn(status.tone === 'warn' ? 'font-medium text-st-warn' : 'text-ink-3')}
        >
          {status.text}
        </p>
        {anyRequired ? (
          <p className="text-ink-3" data-testid="required-legend">
            <span aria-hidden="true">*</span> required column
          </p>
        ) : null}
      </div>
      <RowGrid headers={headers} columns={columns} rows={rows} leading={leading} />
    </div>
  )
}

interface LeadingColumn {
  header: string
  render: (row: string[], index: number) => React.ReactNode
}

const LEADING_COLUMN_PX = 64

function RowGrid({
  headers,
  columns,
  rows,
  leading
}: {
  headers: string[]
  columns: ExtractedColumn[]
  rows: string[][]
  leading?: LeadingColumn
}) {
  const scrollRef = React.useRef<HTMLDivElement>(null)
  const virtual = rows.length > VIRTUALIZE_FROM
  const rowHeight = designPixelsToScaledPixels(ROW_HEIGHT_PX)
  const virtualizer = useVirtualizer({
    count: virtual ? rows.length : 0,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => rowHeight,
    overscan: 12
  })
  const template = React.useMemo(() => {
    const columnsTemplate = columnTemplate(headers, rows)
    return leading ? `${designPixelsToRem(LEADING_COLUMN_PX)} ${columnsTemplate}` : columnsTemplate
  }, [headers, rows, leading])
  const declared = React.useMemo(
    () => new Map(columns.map((column) => [column.name, column])),
    [columns]
  )

  const renderRow = (row: string[], index: number, style?: React.CSSProperties) => (
    <div
      key={index}
      role="row"
      className="grid border-b border-line/60 last:border-0"
      style={{ gridTemplateColumns: template, height: rowHeight, ...style }}
    >
      {leading ? (
        <div role="cell" className="truncate px-2 leading-7">
          {leading.render(row, index)}
        </div>
      ) : null}
      {headers.map((_, cellIndex) => {
        const cell = row[cellIndex] ?? ''
        return (
          <div
            key={cellIndex}
            role="cell"
            className="truncate px-2 leading-7 font-mono"
            title={cell}
          >
            {cell}
          </div>
        )
      })}
    </div>
  )

  return (
    <div
      ref={scrollRef}
      role="table"
      // isolate keeps this grid's sticky header (z-10, above the virtualizer's
      // absolute rows) from painting over the job title bar (z-5) when the
      // grid scrolls underneath it.
      className="relative isolate max-h-[70vh] overflow-auto rounded-md border border-line text-11"
    >
      <div role="rowgroup" className="min-w-max">
        <div
          role="row"
          className="sticky top-0 z-10 grid border-b border-line bg-bg-sunk"
          style={{ gridTemplateColumns: template }}
        >
          {leading ? (
            <div role="columnheader" className="px-2 py-1.5 font-medium whitespace-nowrap text-ink">
              {leading.header}
            </div>
          ) : null}
          {headers.map((name) => {
            const column = declared.get(name)
            return (
              <div
                key={name}
                role="columnheader"
                className="px-2 py-1.5 font-medium whitespace-nowrap text-ink"
              >
                {name}
                {column?.required ? (
                  <abbr title="required column" className="ml-0.5 no-underline">
                    *
                  </abbr>
                ) : null}
                <span className="block font-mono text-10 font-normal text-ink-3">
                  {column?.type ?? 'VARCHAR'}
                </span>
              </div>
            )
          })}
        </div>
        {rows.length === 0 ? (
          <p className="px-2 py-3 text-ink-3">No rows.</p>
        ) : virtual ? (
          <div
            role="presentation"
            className="relative"
            style={{ height: virtualizer.getTotalSize() }}
          >
            {virtualizer.getVirtualItems().map((item) => {
              const row = rows[item.index]
              return row
                ? renderRow(row, item.index, {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    transform: `translateY(${item.start}px)`
                  })
                : null
            })}
          </div>
        ) : (
          rows.map((row, index) => renderRow(row, index))
        )}
      </div>
    </div>
  )
}

/** Fixed widths per column so virtual rows line up with the header. */
export function columnTemplate(headers: string[], rows: string[][]): string {
  const sample = rows.slice(0, WIDTH_SAMPLE_ROWS)
  return headers
    .map((header, index) => {
      let longest = header.length + 2
      for (const row of sample) longest = Math.max(longest, (row[index] ?? '').length)
      const px = Math.min(MAX_COLUMN_PX, Math.max(MIN_COLUMN_PX, longest * 7 + 16))
      return designPixelsToRem(px)
    })
    .join(' ')
}
